import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Atom, Sigma, FlaskConical, Timer, CheckCircle2, XCircle, Lock, CalendarCheck, RotateCcw, Loader2, Trophy } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { "Φυσική": Atom, "Μαθηματικά": Sigma, "Χημεία": FlaskConical };
const DIFF = { 1: "Βασικό", 2: "Μεσαίο", 3: "Απαιτητικό" };
const inputCls = "w-full h-11 px-3 border border-border rounded-sm bg-white text-sm focus:outline-none focus:border-primary";
const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function Header() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/10">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="quiz-logo">
          <Logo className="h-10 w-12" ringColor="#0A1A4A" />
          <div className="leading-none">
            <div className="font-heading font-extrabold text-lg">Φυσική Επιλογή</div>
            <div className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Διαγνωστικό Τεστ</div>
          </div>
        </Link>
        <Link to="/" data-testid="quiz-back" className="inline-flex items-center gap-2 font-mono-accent text-xs uppercase tracking-widest text-foreground/70 hover:text-primary"><ArrowLeft className="h-4 w-4" /> Αρχική</Link>
      </div>
    </header>
  );
}

function Picker({ meta, onStart }) {
  const [subject, setSubject] = useState(null);
  const [grade, setGrade] = useState(null);
  const ok = subject && grade && meta.available.find((a) => a.subject === subject && a.grade === grade)?.count > 0;
  return (
    <div data-testid="quiz-picker">
      <div className="font-mono-accent text-xs uppercase tracking-[0.2em] text-accent mb-3">// Δωρεάν διαγνωστικό τεστ</div>
      <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Δες πού βρίσκεσαι σε {meta.minutes} λεπτά.</h1>
      <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl">{meta.size} ερωτήσεις πολλαπλής επιλογής, 3 επίπεδα δυσκολίας, χρονόμετρο {meta.minutes}'. Στο τέλος βλέπεις σκορ, ανάλυση ανά ενότητα και τις σωστές λύσεις.</p>
      <div className="mt-10 font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-3">1. Μάθημα</div>
      <div className="grid sm:grid-cols-3 gap-3">
        {meta.subjects.map((s) => { const Icon = ICONS[s]; const on = subject === s; return (
          <button key={s} data-testid={`quiz-subject-${s}`} onClick={() => setSubject(s)} className={`flex items-center gap-3 p-4 border rounded-sm text-left transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"}`}>
            <Icon className="h-6 w-6" strokeWidth={1.5} /><span className="font-heading font-bold">{s}</span>
          </button>); })}
      </div>
      <div className="mt-6 font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-3">2. Τάξη</div>
      <div className="grid sm:grid-cols-2 gap-3">
        {meta.grades.map((g) => { const on = grade === g; return (
          <button key={g} data-testid={`quiz-grade-${g}`} onClick={() => setGrade(g)} className={`p-4 border rounded-sm font-heading font-bold text-left transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"}`}>{g}</button>); })}
      </div>
      <button data-testid="quiz-start" disabled={!ok} onClick={() => onStart(subject, grade)} className="mt-8 inline-flex items-center gap-2 h-12 px-8 bg-primary text-primary-foreground font-mono-accent text-xs uppercase tracking-widest rounded-sm hover:bg-accent transition-colors disabled:opacity-40">
        Ξεκίνα το τεστ <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

function Runner({ subject, grade, data, onFinish }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [left, setLeft] = useState(data.minutes * 60);
  const started = useRef(Date.now());
  const qs = data.questions;
  const q = qs[idx];
  const answered = Object.keys(answers).length;

  const finish = () => onFinish(answers, Math.round((Date.now() - started.current) / 1000));
  useEffect(() => {
    const t = setInterval(() => setLeft((l) => l - 1), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (left <= 0) { toast.warning("Τέλος χρόνου! Υποβάλλονται οι απαντήσεις σου."); finish(); } }, [left]); // eslint-disable-line

  return (
    <div data-testid="quiz-runner">
      <div className="flex items-center justify-between mb-6">
        <div className="font-mono-accent text-xs uppercase tracking-widest text-muted-foreground">{subject} · {grade}</div>
        <div data-testid="quiz-timer" className={`inline-flex items-center gap-2 font-mono-accent text-sm px-3 py-1.5 rounded-sm ${left < 120 ? "bg-red-50 text-red-700" : "bg-secondary"}`}><Timer className="h-4 w-4" />{fmtTime(Math.max(left, 0))}</div>
      </div>
      <div className="flex gap-1 mb-8" data-testid="quiz-progress">
        {qs.map((x, i) => <button key={x.id} onClick={() => setIdx(i)} aria-label={`Ερώτηση ${i + 1}`} className={`h-1.5 flex-1 rounded-sm transition-colors ${i === idx ? "bg-accent" : answers[x.id] != null ? "bg-primary" : "bg-border"}`} />)}
      </div>
      <motion.div key={q.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
        <div className="flex items-center gap-3 mb-3">
          <span className="font-heading font-extrabold text-3xl text-primary">{idx + 1}<span className="text-muted-foreground text-lg">/{qs.length}</span></span>
          <span className="font-mono-accent text-[10px] uppercase tracking-widest bg-secondary px-2 py-0.5 rounded-sm">{q.topic}</span>
          <span className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">{DIFF[q.difficulty]}</span>
        </div>
        <h2 data-testid="quiz-question" className="font-heading text-xl md:text-2xl font-bold leading-snug mb-6">{q.question}</h2>
        <div className="grid gap-3">
          {q.options.map((o, i) => { const on = answers[q.id] === i; return (
            <button key={i} data-testid={`quiz-option-${i}`} onClick={() => setAnswers({ ...answers, [q.id]: i })} className={`flex items-center gap-4 p-4 border rounded-sm text-left transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"}`}>
              <span className={`h-8 w-8 shrink-0 flex items-center justify-center rounded-sm font-heading font-bold ${on ? "bg-white/20" : "bg-secondary"}`}>{"ΑΒΓΔ"[i]}</span>
              <span className="text-sm md:text-base">{o}</span>
            </button>); })}
        </div>
      </motion.div>
      <div className="flex items-center justify-between mt-8">
        <button data-testid="quiz-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="inline-flex items-center gap-2 h-11 px-4 border border-border rounded-sm font-mono-accent text-xs uppercase tracking-widest disabled:opacity-30 hover:border-primary"><ArrowLeft className="h-4 w-4" /> Πίσω</button>
        {idx < qs.length - 1 ? (
          <button data-testid="quiz-next" onClick={() => setIdx(idx + 1)} className="inline-flex items-center gap-2 h-11 px-5 bg-primary text-primary-foreground rounded-sm font-mono-accent text-xs uppercase tracking-widest hover:bg-accent">Επόμενη <ArrowRight className="h-4 w-4" /></button>
        ) : (
          <button data-testid="quiz-submit" onClick={() => { if (answered < qs.length && !window.confirm(`Έχεις αφήσει ${qs.length - answered} ερωτήσεις κενές. Υποβολή;`)) return; finish(); }} className="inline-flex items-center gap-2 h-11 px-5 bg-accent text-white rounded-sm font-mono-accent text-xs uppercase tracking-widest hover:bg-primary">Υποβολή ({answered}/{qs.length})</button>
        )}
      </div>
    </div>
  );
}

function Bar({ label, correct, total }) {
  const pct = Math.round(100 * correct / Math.max(total, 1));
  const color = pct >= 70 ? "bg-emerald-600" : pct >= 40 ? "bg-amber-500" : "bg-red-600";
  return (
    <div>
      <div className="flex justify-between text-sm mb-1"><span>{label}</span><span className="font-mono-accent text-xs text-muted-foreground">{correct}/{total}</span></div>
      <div className="h-2 bg-secondary rounded-sm overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function Results({ subject, grade, summary, onRestart }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", school_grade: grade });
  const [loading, setLoading] = useState(false);
  const [full, setFull] = useState(null);
  const pct = Math.round(100 * summary.score / summary.total);
  const verdict = pct >= 80 ? "Εξαιρετικά! Έχεις πολύ καλές βάσεις." : pct >= 60 ? "Καλή εικόνα — με στοχευμένη δουλειά στις αδύναμες ενότητες θα ανέβεις γρήγορα." : pct >= 40 ? "Υπάρχουν κενά που χρειάζονται συστηματική δουλειά — καλό που τα εντοπίσαμε νωρίς." : "Χρειάζεται να χτίσουμε τις βάσεις από την αρχή — και αυτό ακριβώς κάνουμε στη Φυσική Επιλογή.";

  const unlock = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return toast.error("Συμπλήρωσε όνομα, email και τηλέφωνο.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quiz/unlock`, { ...form, result_id: summary.result_id });
      setFull(data); toast.success("Η αναλυτική ανάλυση ξεκλειδώθηκε — σου τη στείλαμε και με email.");
    } catch (err) { toast.error(err.response?.data?.detail || "Κάτι πήγε στραβά."); } finally { setLoading(false); }
  };

  return (
    <div data-testid="quiz-results">
      <div className="bg-primary text-primary-foreground rounded-sm p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
        <div className="text-center md:text-left">
          <div className="font-mono-accent text-[10px] uppercase tracking-widest text-white/60">{subject} · {grade}</div>
          <div data-testid="quiz-score" className="font-heading font-extrabold text-6xl leading-none mt-2">{summary.score}<span className="text-2xl text-white/60">/{summary.total}</span></div>
          <div className="font-mono-accent text-xs text-accent mt-1">{pct}%</div>
        </div>
        <div className="flex-1 border-t md:border-t-0 md:border-l border-white/20 pt-4 md:pt-0 md:pl-6">
          <Trophy className="h-5 w-5 text-accent mb-2" />
          <p className="leading-relaxed">{verdict}</p>
          {summary.weak_topics.length > 0 && <p className="text-sm text-white/70 mt-2">Ενότητες για δουλειά: <strong className="text-white">{summary.weak_topics.join(", ")}</strong></p>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="bg-white border border-border rounded-sm p-5">
          <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Ανά ενότητα</div>
          <div className="space-y-3" data-testid="quiz-topics">{summary.topics.map((t) => <Bar key={t.topic} label={t.topic} correct={t.correct} total={t.total} />)}</div>
        </div>
        <div className="bg-white border border-border rounded-sm p-5">
          <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Ανά δυσκολία</div>
          <div className="space-y-3">{summary.difficulties.map((d) => <Bar key={d.difficulty} label={DIFF[d.difficulty]} correct={d.correct} total={d.total} />)}</div>
        </div>
      </div>

      {!full ? (
        <form onSubmit={unlock} data-testid="quiz-unlock-form" className="mt-6 bg-white border-2 border-primary rounded-sm p-6">
          <div className="flex items-center gap-2 mb-1"><Lock className="h-5 w-5 text-primary" /><h3 className="font-heading font-bold text-lg">Ξεκλείδωσε την αναλυτική ανάλυση</h3></div>
          <p className="text-xs text-muted-foreground mb-4">Δες ποιες ερωτήσεις έκανες λάθος, τη σωστή απάντηση και την επεξήγηση για καθεμία. Θα σου τα στείλουμε και με email.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="quiz-lead-name" className={inputCls} placeholder="Ονοματεπώνυμο *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="quiz-lead-phone" className={inputCls} placeholder="Τηλέφωνο *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <input data-testid="quiz-lead-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input data-testid="quiz-lead-grade" className={inputCls} placeholder="Τάξη" value={form.school_grade} onChange={(e) => setForm({ ...form, school_grade: e.target.value })} />
          </div>
          <button data-testid="quiz-unlock" disabled={loading} className="mt-4 inline-flex items-center gap-2 h-12 px-6 bg-primary text-primary-foreground font-mono-accent text-xs uppercase tracking-widest rounded-sm hover:bg-accent disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Δείξε μου την ανάλυση
          </button>
        </form>
      ) : (
        <div className="mt-6 space-y-3" data-testid="quiz-details">
          {full.details.map((d, i) => (
            <div key={d.id} data-testid={`quiz-detail-${i}`} className={`bg-white border rounded-sm p-4 ${d.correct ? "border-emerald-200" : "border-red-200"}`}>
              <div className="flex items-start gap-3">
                {d.correct ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">{d.topic} · {DIFF[d.difficulty]}</div>
                  <div className="font-medium mt-1">{d.question}</div>
                  <div className="text-sm mt-2">
                    {!d.correct && <div className="text-red-700">Η απάντησή σου: {d.selected != null ? `${"ΑΒΓΔ"[d.selected]}. ${d.options[d.selected]}` : "—"}</div>}
                    <div className="text-emerald-700">Σωστή: {"ΑΒΓΔ"[d.answer]}. {d.options[d.answer]}</div>
                    {d.explanation && <div className="text-muted-foreground mt-1">{d.explanation}</div>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 bg-secondary/60 border border-border rounded-sm p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <div className="font-heading font-bold text-lg flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-primary" /> Συζήτησε τα αποτελέσματα μαζί μας</div>
          <p className="text-sm text-muted-foreground mt-1">Δωρεάν συνάντηση 45' με τον καθηγητή — θα σου προτείνουμε συγκεκριμένο πλάνο για τις ενότητες που χρειάζονται δουλειά.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Link to="/#booking" data-testid="quiz-book" className="inline-flex items-center gap-2 h-11 px-5 bg-primary text-primary-foreground rounded-sm font-mono-accent text-xs uppercase tracking-widest hover:bg-accent">Κλείσε ραντεβού</Link>
          <button data-testid="quiz-restart" onClick={onRestart} className="inline-flex items-center gap-2 h-11 px-4 border border-border rounded-sm font-mono-accent text-xs uppercase tracking-widest hover:border-primary"><RotateCcw className="h-4 w-4" /> Νέο τεστ</button>
        </div>
      </div>
    </div>
  );
}

export default function Quiz() {
  const [meta, setMeta] = useState(null);
  const [stage, setStage] = useState("pick");
  const [sel, setSel] = useState({});
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => { axios.get(`${API}/quiz/meta`).then((r) => setMeta(r.data)).catch(() => toast.error("Δεν φορτώθηκε το τεστ.")); }, []);

  const start = async (subject, grade) => {
    try {
      const { data } = await axios.get(`${API}/quiz/questions`, { params: { subject, grade } });
      setSel({ subject, grade }); setData(data); setStage("run"); window.scrollTo({ top: 0 });
    } catch { toast.error("Δεν υπάρχουν ερωτήσεις για αυτή την επιλογή."); }
  };

  const finish = async (answers, duration_sec) => {
    const full = Object.fromEntries(data.questions.map((q) => [q.id, answers[q.id] ?? null]));
    try {
      const { data: s } = await axios.post(`${API}/quiz/submit`, { ...sel, answers: full, duration_sec });
      setSummary(s); setStage("done"); window.scrollTo({ top: 0 });
    } catch { toast.error("Η υποβολή απέτυχε. Δοκίμασε ξανά."); }
  };

  const view = useMemo(() => {
    if (!meta) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Φόρτωση…</div>;
    if (stage === "pick") return <Picker meta={meta} onStart={start} />;
    if (stage === "run") return <Runner subject={sel.subject} grade={sel.grade} data={data} onFinish={finish} />;
    return <Results subject={sel.subject} grade={sel.grade} summary={summary} onRestart={() => { setStage("pick"); setSummary(null); }} />;
  }, [meta, stage, sel, data, summary]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-background font-body text-foreground">
      <Header />
      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12">{view}</main>
    </div>
  );
}
